import { useMemo } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputField from "../../../../InputField/InputField";
import { FrequencyType } from "../../../../../enums/Order";
import styles from "./TestFrequencyInputs.module.scss";

interface TestFrequencyInputsProps {
  className?: string;
  hideLabel?: boolean;
  isTestSelected: boolean;
  frequency: FrequencyType | undefined;
  frequencyReps: number | undefined;
  onChange: (value: {
    frequency?: FrequencyType;
    frequencyReps?: number;
  }) => void;
}

const TestFrequencyInputs = (props: TestFrequencyInputsProps) => {
  const {
    className,
    hideLabel,
    isTestSelected,
    frequency,
    frequencyReps,
    onChange,
  } = props;

  const showRepsError = isTestSelected && !frequencyReps;

  // Determine if we should show custom input (frequency is Custom OR reps > 7)
  const isCustomFrequency = frequency === FrequencyType.Custom || (frequencyReps && frequencyReps > 7);

  // For dropdown display: if custom or > 7, show "custom", otherwise show the number
  const dropdownValue = useMemo(() => {
    if (isCustomFrequency) return "custom";
    return frequencyReps !== undefined ? String(frequencyReps) : "";
  }, [isCustomFrequency, frequencyReps]);

  const dropdownOptions = useMemo(
    () => [
      { label: "1", value: "1" },
      { label: "2", value: "2" },
      { label: "3", value: "3" },
      { label: "4", value: "4" },
      { label: "5", value: "5" },
      { label: "6", value: "6" },
      { label: "7", value: "7" },
      { label: "Custom", value: "custom" },
    ],
    []
  );

  const handleDropdownChange = (value: string) => {
    if (value === "custom") {
      onChange({
        frequency: FrequencyType.Custom,
        frequencyReps: undefined, // Clear the value so user can enter custom
      });
    } else {
      onChange({
        frequency: FrequencyType.Weekly, // Always use Weekly for API
        frequencyReps: Number(value),
      });
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className ?? ""}`}>
      {!hideLabel && (
        <label
          className={`text-base font-medium ${
            !frequencyReps ? "text-crimson" : "text-cloudBurst"
          }`}
        >
          Times per Week *
        </label>
      )}

      <div className="flex items-center gap-1">
        <div className={styles.select_wrapper} style={{ width: '120px', minWidth: '120px' }}>
          <Select
            fullWidth
            MenuProps={{
              classes: { root: styles.select_root },
            }}
            value={dropdownValue}
            onChange={({ target: { value } }) => handleDropdownChange(value)}
            error={showRepsError}
          >
            {dropdownOptions.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </div>

        {isCustomFrequency && (
          <div className="flex items-center gap-1 ml-2.5">
            <InputField
              hideErrorMessage
              className="w-12"
              required
              isNumericField
              error={showRepsError}
              placeholder="0"
              value={
                frequencyReps !== undefined ? String(frequencyReps) : undefined
              }
              onChange={({ target: { value } }) =>
                onChange({
                  frequency: FrequencyType.Custom,
                  frequencyReps: value.length ? Number(value) : undefined,
                })
              }
            />
            <span className="text-xs text-cloudBurst whitespace-nowrap">per week</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestFrequencyInputs;
